import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import * as BABYLON from 'babylonjs';
import 'babylonjs-loaders';
import * as GUI from 'babylonjs-gui';
import GLBConverter from '../Utilities/GLBConverter';
import FacilityStore from '../stores/FacilityStore';
import ViewerStore from '../stores/ViewerStore';
import * as Inovx from '@shared';
import ViewerActions from '../actions/ViewerActions';
import AssemblyLibraryStore from '../stores/AssemblyLibraryStore';
interface BabylonViewerProps {
    onImageLoaded?: () => void;
    onLoadingChange?: (loading: boolean) => void;
    onUploadStarted?: () => void;
    disabled?: boolean;
}
export interface BabylonViewerHandle {
    generateThumbnail: () => Promise<string>;
    generateSceneGLB: () => Promise<ArrayBuffer>;
    loadGLBIntoScene: (assemblyID: string, assemblyURL: string) => void;
    handleFileUploadClick?: () => void;
}

const BabylonViewer = forwardRef<BabylonViewerHandle, BabylonViewerProps>((props, ref) => {
    var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

    const xShiftRef = useRef<number>(null);
    const yShiftRef = useRef<number>(null);
    //var glbOriginalScale: BABYLON.Vector3;
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const engineRef = useRef<BABYLON.Engine | null>(null);
    const sceneRef = useRef<BABYLON.Scene | null>(null);
    const axesRef = useRef<BABYLON.AxesViewer | null>(null);
    const previewCameraRef = useRef<BABYLON.ArcRotateCamera | null>(null);
    const axesCameraRef = useRef<BABYLON.ArcRotateCamera | null>(null);
    const textureRef = useRef<GUI.AdvancedDynamicTexture | null>(null);
    const [fileLoaded, setFileLoaded] = useState(false);

    const [assemblyPosition, setAssemblyPosition] = useState<IVector3D>(AssemblyLibraryStore.getAssemblyPosition());
    const [assemblyScale, setAssemblyScale] = useState<number>(AssemblyLibraryStore.getAssemblyScale());
    const glbConverter = new GLBConverter();
    const anchorSphere = useRef<BABYLON.Mesh | null>(null);
    const glbMeshes = useRef<BABYLON.AbstractMesh[] | null>(null);
    const rootNode = useRef<BABYLON.TransformNode | null>(null);
    const glbOriginalScale = useRef<BABYLON.Vector3 | null>(null);
    const rootScaleNodeName = "rootScaleNode";
    const glbMeshName = "glbimportmesh";
    const fileInputRef = useRef<HTMLInputElement>(null);
    const loadSequence = useRef(0);
    const pendingLoad = useRef<Promise<void>>(Promise.resolve());
    const loading = useRef(false);

    useImperativeHandle(ref, () => ({
        generateThumbnail,
        generateSceneGLB,
        loadGLBIntoScene,
        handleFileUploadClick
    }));

    useEffect(() => {
        if (canvasRef.current) {
            AssemblyLibraryStore.addChangeListener(onAssemblyStoreChange);
            // Initialize Babylon.js engine and scene
            engineRef.current = new BABYLON.Engine(canvasRef.current, true);
            sceneRef.current = new BABYLON.Scene(engineRef.current);

            var envTexture = BABYLON.CubeTexture.CreateFromPrefilteredData(
                Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + "/Assemblies/environmentSpecular.env",
                sceneRef.current
            );
            sceneRef.current.environmentTexture = envTexture;

            // Create a simple environment
            const camera = new BABYLON.ArcRotateCamera("camera", Math.PI / 2, Math.PI / 2, 2, BABYLON.Vector3.Zero(), sceneRef.current);
            previewCameraRef.current = camera;
            camera.attachControl(canvasRef.current, true);
            camera.lowerRadiusLimit = 0;

            const ambientLight = new BABYLON.HemisphericLight("ambientLight", new BABYLON.Vector3(0, 1, 0), sceneRef.current);
            ambientLight.intensity = 1.0;

            // GUI Overlay setup
            textureRef.current = GUI.AdvancedDynamicTexture.CreateFullscreenUI("UI");

            // Rectangle for bounding box information
            const boundingBoxRect = new GUI.Rectangle();
            boundingBoxRect.width = "150px";
            boundingBoxRect.height = "95px";
            boundingBoxRect.cornerRadius = 10;
            boundingBoxRect.color = "white";
            boundingBoxRect.thickness = 2;
            boundingBoxRect.background = "rgba(0, 0, 0, 0.3)";
            //boundingBoxRect.background = "rgba(200, 200, 200, 0.9)"; // Lighter grey background
            boundingBoxRect.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
            boundingBoxRect.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_TOP;
            boundingBoxRect.top = "10px";
            boundingBoxRect.left = "-10px";

            // Title text block aligned at the top
            const titleBlock = new GUI.TextBlock();
            titleBlock.text = "Bounding Box";
            titleBlock.color = "white";
            titleBlock.fontSize = 14;
            titleBlock.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
            titleBlock.textVerticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_TOP;
            titleBlock.paddingTop = "10px";
            boundingBoxRect.addControl(titleBlock);

            // Height text block (green) centered
            const heightBlock = new GUI.TextBlock("heightBlock");
            heightBlock.color = "green";
            heightBlock.fontSize = 12;
            heightBlock.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
            heightBlock.textVerticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_TOP;
            heightBlock.top = "35px"; // Adjusted to be below the title
            boundingBoxRect.addControl(heightBlock);

            // Width text block (blue) centered
            const widthBlock = new GUI.TextBlock("widthBlock");
            widthBlock.color = "blue";
            widthBlock.fontSize = 12;
            widthBlock.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
            widthBlock.textVerticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_TOP;
            widthBlock.top = "50px";
            boundingBoxRect.addControl(widthBlock);

            // Length text block (red) centered
            const lengthBlock = new GUI.TextBlock("lengthBlock");
            lengthBlock.color = "red";
            lengthBlock.fontSize = 12;
            lengthBlock.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
            lengthBlock.textVerticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_TOP;
            lengthBlock.top = "65px";
            boundingBoxRect.addControl(lengthBlock);

            // Add the bounding box rectangle to the GUI
            textureRef.current.addControl(boundingBoxRect);

            // Run the render loop
            engineRef.current.runRenderLoop(() => {
                sceneRef.current?.render();
            });

            // Force the engine to resize the canvas to match the parent container
            engineRef.current.resize();

            // Handle window resize
            window.addEventListener('resize', () => {
                engineRef.current?.resize();
            });

            const handleWheel = (event: WheelEvent) => {
                event.preventDefault();
            };

            canvasRef.current.addEventListener("wheel", handleWheel, { passive: false });

            // Cleanup
            return () => {
                loadSequence.current++;
                AssemblyLibraryStore.removeChangeListener(onAssemblyStoreChange);
                engineRef.current?.dispose();
                sceneRef.current?.dispose();
                textureRef.current.dispose();
            };
        }
    }, []);

    useEffect(() => {
        updateSceneScaleAndPosition(assemblyScale, assemblyPosition);
    }, [assemblyPosition]);

    useEffect(() => {
        updateSceneScaleAndPosition(assemblyScale, assemblyPosition);
    }, [assemblyScale]);

    const setTopBottomRatio = (camera: BABYLON.ArcRotateCamera) => {
        const ratio = canvasRef.current.height / canvasRef.current.width;
        camera.orthoTop = camera.orthoRight * ratio;
        camera.orthoBottom = camera.orthoLeft * ratio;
    };

    let zoomTarget: any = null;
    const onAssemblyStoreChange = () => {
        setAssemblyPosition(AssemblyLibraryStore.getAssemblyPosition());
        setAssemblyScale(AssemblyLibraryStore.getAssemblyScale());
    };
    const resetCameraZoom = (camera: BABYLON.ArcRotateCamera) => {
        camera.setPosition(new BABYLON.Vector3(0, 0, -100));
        camera.target = new BABYLON.Vector3(0, 0, 0);
        camera.orthoLeft = -8;
        camera.orthoRight = 8;
        setTopBottomRatio(camera);
    };

    const updateSceneScaleAndPosition = (scale: number, position:IVector3D) => {
        if (position != null && anchorSphere.current != null) {
            anchorSphere.current.position.x = position.X;
            anchorSphere.current.position.y = position.Y;
            anchorSphere.current.position.z = position.Z;
        }

        if (scale != null && anchorSphere.current != null && rootNode.current != null) {
            const scaleFactor = scale;
            rootNode.current.scaling = new BABYLON.Vector3(scaleFactor * glbOriginalScale.current.x, scaleFactor * glbOriginalScale.current.y, scaleFactor * glbOriginalScale.current.z);
            var bounds = calculateBoundingBox();
            var camera = previewCameraRef.current;
            camera.zoomOn(glbMeshes.current);

            const extentSize = Math.max(bounds.boundingBox.extendSize.x, bounds.boundingBox.extendSize.y, bounds.boundingBox.extendSize.z); // Use largest dimension
            const zoomFactor = 100 / extentSize; // Adjust this scaling factor as needed

            var maxBase = Math.max(bounds.boundingBox.extendSize.x, bounds.boundingBox.extendSize.z);

            anchorSphere.current.scaling = new BABYLON.Vector3(maxBase / 10, maxBase / 10, maxBase / 10);

            camera.wheelPrecision = zoomFactor;
            camera.panningSensibility = zoomFactor * 10;
        }
    }
    const calculateAxesShift = () => {

        engineRef.current.resize();

        const screenClickX = canvasRef.current.width / 10;
        const screenClickY = canvasRef.current.height - canvasRef.current.height / 10;

        // Project the screen point (0, 0) into world space at a given distance along the forward vector of the camera
        const projectedPoint = BABYLON.Vector3.Unproject(
            new BABYLON.Vector3(screenClickX, screenClickY, 1), // click point on screen with normalized z depth of 1
            sceneRef.current.getEngine().getRenderWidth(),
            sceneRef.current.getEngine().getRenderHeight(),
            BABYLON.Matrix.Identity(),
            axesCameraRef.current.getViewMatrix(),
            axesCameraRef.current.getProjectionMatrix()
        );

        // Calculate the difference between the camera's current target point and the projected point
        const shiftX = projectedPoint.x - axesCameraRef.current.position.x;
        const shiftY = projectedPoint.y - axesCameraRef.current.position.y;

        var xShift = -1 * shiftX;
        var yShift = -1 * shiftY;

        xShiftRef.current = xShift;
        yShiftRef.current = yShift;
    }
    const calculateBoundingBox = () => {
        if (rootNode.current) {
            
            calculateAxesShift();
            const bounds = rootNode.current.getHierarchyBoundingVectors(true);
            const boundBox = new BABYLON.BoundingInfo(bounds.min, bounds.max);
            const dimensions = boundBox.boundingBox.extendSize;
            const length = (dimensions.z * 2).toFixed(2);
            const width = (dimensions.x * 2).toFixed(2);
            const height = (dimensions.y * 2).toFixed(2);

            const advancedTexture = textureRef.current;
            const heightBlock = advancedTexture.getControlByName("heightBlock") as GUI.TextBlock;
            const widthBlock = advancedTexture.getControlByName("widthBlock") as GUI.TextBlock;
            const lengthBlock = advancedTexture.getControlByName("lengthBlock") as GUI.TextBlock;

            heightBlock.text = `Height: ${height}` + " m";
            widthBlock.text = `Width: ${width}` + " m";
            lengthBlock.text = `Length: ${length}` + " m";

            var camera = previewCameraRef.current;

            if (dimensions.x * 2 < 1 || dimensions.y * 2 < 1 || dimensions.z * 2 < 1) {
                camera.minZ = .001;
            }
            else {
                camera.minZ = 1;
            }

            camera.maxZ = 10000;


            return boundBox;
        }
    };
    const setupRenderLoop = () => {
        engineRef.current.stopRenderLoop();

        var alphaShift = Math.PI;
        var p = axesCameraRef.current.position.clone();
        var camera = previewCameraRef.current;

        const setAxisViewer = (camera: BABYLON.ArcRotateCamera, position: BABYLON.Vector3) => {

            calculateAxesShift();
            let right = new BABYLON.Vector3(0, 0, 1);
            let up = new BABYLON.Vector3(0, 1, 0);
            let forward = new BABYLON.Vector3(1, 0, 0);
            var alpha = camera.alpha + alphaShift;
            var beta = camera.beta - Math.PI / 2;
            // Create rotation matrices
            const rotationY = BABYLON.Matrix.RotationY(alpha); // Rotate around Y-axis by alpha
            const rotationX = BABYLON.Matrix.RotationX(beta);  // Rotate around X-axis by beta

            // Apply rotations sequentially
            right = BABYLON.Vector3.TransformCoordinates(right, rotationY).normalize();
            right = BABYLON.Vector3.TransformCoordinates(right, rotationX).normalize();

            up = BABYLON.Vector3.TransformCoordinates(up, rotationY).normalize();
            up = BABYLON.Vector3.TransformCoordinates(up, rotationX).normalize();

            forward = BABYLON.Vector3.TransformCoordinates(forward, rotationY).normalize();
            forward = BABYLON.Vector3.TransformCoordinates(forward, rotationX).normalize();
            axesRef.current.update(new BABYLON.Vector3(position.x - xShiftRef.current, position.y - yShiftRef.current, -10),
                forward.scale(-1),
                up,
                right.scale(-1)
            );
        };

        engineRef.current.runRenderLoop(() => {
            sceneRef.current?.render();
            setAxisViewer(camera, p);
        });
    };
    const setupGLBScene = () => {
        if (glbMeshes.current.length > 0 && glbMeshes.current[0].name == "__root__") {
            rootNode.current = glbMeshes.current[0];
            glbOriginalScale.current = rootNode.current.scaling.clone();

        }
        else {
            // This is untested, as no glb imports without __root__ as the head element in glbMeshes.
            rootNode.current = new BABYLON.TransformNode(rootScaleNodeName, sceneRef.current);
            rootNode.current.scaling = new BABYLON.Vector3(1, 1, 1);
            glbOriginalScale.current = rootNode.current.scaling.clone();
        }

        sceneRef.current.clearColor = new BABYLON.Color4(0.9, 0.9, 0.9, 1);
        setFileLoaded(true);
    
        var camera = previewCameraRef.current;

        const axis = new BABYLON.Debug.AxesViewer(sceneRef.current, 1.0, 0);
        
        axis.xAxis.getChildMeshes().forEach((mesh) => {
            mesh.layerMask = 0x10000000;
        });
        axis.yAxis.getChildMeshes().forEach((mesh) => {
            mesh.layerMask = 0x10000000;
        });
        axis.zAxis.getChildMeshes().forEach((mesh) => {
            mesh.layerMask = 0x10000000;
        });

        axesRef.current = axis;
        engineRef.current.resize();

        var camera2 = new BABYLON.ArcRotateCamera('Camera2', 0, 0, 1, new BABYLON.Vector3(1, 0, 0), sceneRef.current);
        axesCameraRef.current = camera2;
        resetCameraZoom(camera2);
        camera2.layerMask = 0x10000000;
        camera2.mode = BABYLON.Camera.ORTHOGRAPHIC_CAMERA;
        camera2.position = new BABYLON.Vector3(0, 0, -1);
        camera2.target = new BABYLON.Vector3(0, 0, 0);
        camera2.alpha += Math.PI;
        camera2.getViewMatrix(true);

        var bounds = calculateBoundingBox();

        const canvas = sceneRef.current.getEngine().getRenderingCanvas();
        
        var p = camera2.position.clone();

        var xScale = 1;
        var zScale = 1;
        var alphaShift = Math.PI;

        const projectToScreen = (camera: BABYLON.Camera, scene: BABYLON.Scene, position: BABYLON.Vector3) => {
            const engine = scene.getEngine();
            const transformMatrix = camera.getTransformationMatrix();
            const viewport = camera.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight());
            const projectedPosition = BABYLON.Vector3.Project(
                position,
                BABYLON.Matrix.Identity(),
                transformMatrix,
                viewport
            );
            return new BABYLON.Vector2(projectedPosition.x, projectedPosition.y);
        };
        
        sceneRef.current.activeCameras.push(camera, camera2);

        setupRenderLoop();

        var anchorMaterial = new BABYLON.StandardMaterial("AnchorMaterial", sceneRef.current);
        anchorMaterial.diffuseColor = new BABYLON.Color3(1, 0, 0); // Solid red color
        anchorMaterial.emissiveColor = new BABYLON.Color3(1, 0, 0); // Ensure it appears fully red regardless of lighting
        anchorMaterial.disableLighting = true; // Disable lighting so it doesn't receive shadows
        anchorMaterial.specularColor = new BABYLON.Color3(0, 0, 0); // No shininess or reflections
        anchorMaterial.id = "AnchorMaterial";

        const sphere = BABYLON.MeshBuilder.CreateSphere("AnchorSphere", { diameter: 1.0 }, sceneRef.current);
        sphere.material = anchorMaterial;
        anchorMaterial.disableDepthWrite = true;
        sphere.renderingGroupId = 1;
        sphere.id = "AnchorSphere";

        anchorSphere.current = sphere;

        if (bounds) {

            const extentSize = Math.max(bounds.boundingBox.extendSize.x, bounds.boundingBox.extendSize.y, bounds.boundingBox.extendSize.z); // Use largest dimension
            const zoomFactor = 100 / extentSize; // Adjust this scaling factor as needed

            var maxBase = Math.max(bounds.boundingBox.extendSize.x, bounds.boundingBox.extendSize.z);

            sphere.scaling = new BABYLON.Vector3(maxBase / 10, maxBase / 10, maxBase / 10);
            camera.wheelPrecision = zoomFactor;
            camera.panningSensibility = zoomFactor * 10;
        }
        const meshesWithGeometry = glbMeshes.current.filter(mesh => (mesh as BABYLON.Mesh).geometry != null);

        camera.zoomOn(meshesWithGeometry);
        camera.alpha += Math.PI / 4;
        updateSceneScaleAndPosition(AssemblyLibraryStore.getAssemblyScale(), AssemblyLibraryStore.getAssemblyPosition());
    }
    const loadGLBIntoScene = async (assemblyID: string, assemblyURL: string) => {
        // Only load if the scene is ready AND there is not already another glb loaded. 
        if (sceneRef.current && glbMeshes.current == null && !loading.current) {
            await replaceGLB(assemblyURL, false);
        }
    }
    const replaceGLB = (url: string, uploaded: boolean): Promise<void> => {
        const sequence = ++loadSequence.current;
        const scene = sceneRef.current;
        loading.current = true;
        props.onLoadingChange?.(true);
        pendingLoad.current = pendingLoad.current.then(async () => {
            try {
                if (sequence !== loadSequence.current || !scene || scene.isDisposed) return;
                scene.getEngine().stopRenderLoop();
                axesRef.current?.dispose();
                axesRef.current = null;
                anchorSphere.current?.dispose(false, true);
                anchorSphere.current = null;
                axesCameraRef.current?.dispose();
                axesCameraRef.current = null;
                scene.activeCameras = [];
                glbMeshes.current?.forEach(mesh => { if (!mesh.isDisposed()) mesh.dispose(false, true); });
                if (rootNode.current && !rootNode.current.isDisposed()) rootNode.current.dispose();
                rootNode.current = null;
                glbMeshes.current = null;
                setFileLoaded(false);
                const meshes = await glbConverter.loadGLBUrlIntoScene(scene, glbMeshName, url, true);
                if (sequence !== loadSequence.current || scene.isDisposed) {
                    meshes.forEach(mesh => { if (!mesh.isDisposed()) mesh.dispose(false, true); });
                    return;
                }
                if (!meshes.length) return;
                glbMeshes.current = meshes;
                setupGLBScene();
                if (uploaded) props.onImageLoaded?.();
            } catch (error) {
                console.error('Error loading GLB file:', error);
            } finally {
                if (uploaded) URL.revokeObjectURL(url);
                if (sequence === loadSequence.current) {
                    loading.current = false;
                    props.onLoadingChange?.(false);
                }
            }
        });
        return pendingLoad.current;
    };
    const generateSceneGLB = async (): Promise<ArrayBuffer> => {
        await pendingLoad.current;
        if (!glbMeshes.current?.length) return undefined;

        const engine = sceneRef.current.getEngine();
        sceneRef.current?.render();
        engine.stopRenderLoop();

        const rootTransformNode = rootNode.current;
        var currentScaling = rootTransformNode.scaling.clone();
        rootTransformNode.scaling = glbOriginalScale.current;

        // Store original parent relationships
        const originalParents = new Map<BABYLON.AbstractMesh, BABYLON.AbstractMesh | null>();

        // Detach children of the TransformNode
        rootTransformNode.getChildren().forEach(child => {
            {
                originalParents.set(child as any, (child as any).parent);
                (child as any).setParent(null); // Detach from the TransformNode
            }
        });
        try {
            //sceneRef.current.removeTransformNode(rootTransformNode);
            sceneRef.current.removeMesh(glbMeshes.current[0]);
            // Export and compress the scene without the TransformNode
            const compressedContent = await glbConverter.compressSceneToBuffer(sceneRef.current, glbMeshName);

            return compressedContent;

        } finally {

            setupRenderLoop();
            sceneRef.current.addMesh(glbMeshes.current[0]);

            originalParents.forEach((parent, child) => {
                child.setParent(parent);
                
                child.computeWorldMatrix(true);
            });

            rootTransformNode.scaling = currentScaling;
            glbMeshes.current[0].computeWorldMatrix(true);
        }
    }
    const generateThumbnail = async (): Promise<string> => {
        await pendingLoad.current;
        if (!glbMeshes.current) return "";

        const thumbnailSize = 150;
        const renderTarget = new BABYLON.RenderTargetTexture("thumbnail", { width: thumbnailSize, height: thumbnailSize }, sceneRef.current, false);

        // Create a temporary camera for thumbnail capture
        const thumbnailCamera = new BABYLON.ArcRotateCamera("thumbnailCamera", Math.PI / 2, Math.PI / 2.2, 10, BABYLON.Vector3.Zero(), sceneRef.current);
        renderTarget.activeCamera = thumbnailCamera;

        const meshesWithGeometry = glbMeshes.current.filter(mesh => (mesh as BABYLON.Mesh).geometry != null);

        // Set the camera to frame all meshes in the scene
        thumbnailCamera.zoomOn(meshesWithGeometry);

        renderTarget.renderList = glbMeshes.current;

        // Wrap the thumbnail generation in a Promise
        return new Promise((resolve) => {
            renderTarget.onAfterRenderObservable.add(async () => {
                const textureData = renderTarget.readPixels();
                if (textureData) {
                    const bufferData = (await textureData).buffer;
                    const canvas = document.createElement("canvas");
                    canvas.width = thumbnailSize;
                    canvas.height = thumbnailSize;
                    const context = canvas.getContext("2d");

                    if (context) {
                        const pixelData = Uint8ClampedArray.from(new Uint8Array(bufferData));
                        const imageData = new ImageData(pixelData, thumbnailSize, thumbnailSize);

                        // Flip the image vertically for correct orientation
                        for (let y = 0; y < thumbnailSize / 2; y++) {
                            for (let x = 0; x < thumbnailSize; x++) {
                                const topIndex = (y * thumbnailSize + x) * 4;
                                const bottomIndex = ((thumbnailSize - y - 1) * thumbnailSize + x) * 4;
                                for (let i = 0; i < 4; i++) {
                                    const temp = imageData.data[topIndex + i];
                                    imageData.data[topIndex + i] = imageData.data[bottomIndex + i];
                                    imageData.data[bottomIndex + i] = temp;
                                }
                            }
                        }

                        context.putImageData(imageData, 0, 0);
                        const thumbnailUrl = canvas.toDataURL("image/png");

                        // Resolve the promise with the generated thumbnail URL
                        resolve(thumbnailUrl);
                    }
                }
                // Remove the temporary camera after rendering
                sceneRef.current?.removeCamera(thumbnailCamera);
                renderTarget.dispose();
            });

            renderTarget.render();
        });
    };

    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const file = event.dataTransfer.files?.[0];
        if (file) await handleFileUpload(file);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };
    const handleFileUpload = async (file: File) => {
        if (props.disabled) return;
        if (file && file.name.toLowerCase().endsWith('.glb')) {
            props.onUploadStarted?.();
            await replaceGLB(URL.createObjectURL(file), true);
        }
    };
    const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = ''; // Allow selecting the same file again.
        if (file) {
            await handleFileUpload(file);
        }
    };
    
    const handleFileUploadClick = () => {
        if (props.disabled) return;
        if (fileInputRef.current) {
            fileInputRef.current.click(); // Trigger hidden file input
        }
    };
    return (
        <div
            onWheel={(e) => e.stopPropagation()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            style={{ width: '100%', height: '100%', position: 'relative' }} // No dashed border here
        >
            {/* Overlay text and icon when no file is loaded */}
            {!fileLoaded && (
                <div className="sub-box" onClick={handleFileUploadClick}>
                    <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/icons/upload.png`} />
                    <div className="text-black sub-title">{InovxLoader.t('facility:viewer.uploadModel')}</div>
                </div>
            )}
            <input
                type="file"
                ref={fileInputRef}
                accept=".glb"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
            />
            {/* Canvas for rendering the 3D scene */}
            <canvas
                ref={canvasRef}
                tabIndex={0} // Enable focus for the canvas
                style={{
                    width: '100%',
                    height: '100%',
                    outline: 'none', // Completely remove the focus outline
                }}
            ></canvas>


        </div>
    );
});

export default BabylonViewer;
